import posthog from 'posthog-js';

// Failure events that belong in PostHog Error Tracking — genuine bugs or broken
// capabilities we'd want an alert for. Everything else reportError captures is
// analytics-only: e.g. recording_empty (user behaviour),
// tts_no_english / tts_english_failed (device configuration), and
// recording_quiet is not routed through reportError at all (normal on iOS).
// recording_silent is tracked: an iPhone mic that records nothing after the
// first take looks exactly like it, and it stayed invisible until Nov 2026.
const ERROR_TRACKED = new Set([
    'mic_denied',
    'mic_missing',
    'mic_failed',
    'mic_unsupported',
    'recording_silent',
    'analysis_failed',
    'tts_failed',
    'tts_natural_voice_fallback',
]);

// Report a recording / mic / TTS failure to:
//   1. the analytics feed as a named event (readable at a glance), always, and
//   2. PostHog Error Tracking as a grouped $exception — only for names in
//      ERROR_TRACKED — so real failures show up in the Issues list and can
//      trigger alerts (email/Slack) instead of being lost in the Activity
//      firehose.
// `name` doubles as the exception message so each scenario groups into its own
// Issue (e.g. every "mic_denied" collapses into one issue with a count). Events
// are not mode-suffixed — pass mode: "manual"/"automatic" in props instead, so
// each failure type is a single event/Issue filterable by mode.
// Phone or laptop, and which browser, in the fewest words that still tell one
// broken device apart from a broken release. The raw user agent is a paragraph
// of noise in a dashboard row.
const deviceSummary = () => {
    try {
        const ua = navigator.userAgent || '';
        const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS'
            : /Android/.test(ua) ? 'Android'
                : /Windows/.test(ua) ? 'Windows'
                    : /Mac OS X/.test(ua) ? 'Mac'
                        : /Linux/.test(ua) ? 'Linux' : 'Unknown';
        const browser = /EdgA?\//.test(ua) ? 'Edge'
            : /SamsungBrowser/.test(ua) ? 'Samsung Internet'
                : /Chrome\//.test(ua) ? 'Chrome'
                    : /Firefox\//.test(ua) ? 'Firefox'
                        : /Safari\//.test(ua) ? 'Safari' : 'Unknown browser';
        // Installed from Play or added to the home screen, which changes what
        // the browser allows and is worth knowing when audio or the mic fails.
        const installed = window.matchMedia?.('(display-mode: standalone)')?.matches ? ' · installed app' : '';
        return `${os} · ${browser}${installed}`;
    } catch {
        return '';
    }
};

// Why a request failed, in one line for the dashboard: the server's own reason
// when it answered, otherwise axios's code ("ERR_NETWORK" means it never got
// there or the connection dropped, "ECONNABORTED" means it timed out).
export const requestFailureReason = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status) return `HTTP ${status}${detail ? `: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}` : ''}`;
    return [err?.code, err?.message].filter(Boolean).join(': ') || 'Unknown error';
};

export const reportError = (name, props = {}) => {
    try { posthog.capture(name, props); } catch { /* analytics best-effort */ }
    if (!ERROR_TRACKED.has(name)) return;
    // Also send it to our own backend, so the admin dashboard shows browser
    // failures next to server ones instead of them living only in PostHog.
    // keepalive so it still goes if the page is closing. Fire and forget: a
    // failure to report a failure must never surface to the user.
    try {
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/client-error`, {
            method: 'POST',
            // The token rides along so the server can say WHICH account hit
            // this, rather than logging an anonymous "some client". It is
            // verified there, never trusted from the body. Absent when logged
            // out, and the report still goes.
            headers: (() => {
                const h = { 'Content-Type': 'application/json' };
                try {
                    const t = localStorage.getItem('token');
                    if (t) h.Authorization = `Bearer ${t}`;
                } catch { /* private mode, report anonymously */ }
                return h;
            })(),
            keepalive: true,
            body: JSON.stringify({
                name,
                message: String(props.error || props.message || ''),
                page: window.location.pathname,
                mode: props.mode || '',
                device: deviceSummary(),
            }),
        }).catch(() => {});
    } catch { /* reporting is best-effort */ }
    try {
        if (typeof posthog.captureException === 'function') {
            const err = new Error(name);
            err.name = name;
            posthog.captureException(err, props);
        }
    } catch { /* error tracking best-effort */ }
};

// What the learner was actually shown after a recording: the respelling, the
// written feedback, and every tip. Without it a complaint like "the tip
// contradicts the respelling" cannot be checked, because only the score was
// logged. Shared so Manual and Automatic log exactly the same fields.
export const feedbackShownProps = (phrase, data = {}) => ({
    phrase_french: phrase?.french || '',
    phrase_phonetic: phrase?.phonetic || '',
    transcript_heard: data.transcript_heard || '',
    feedback_text: data.feedback || '',
    audio_issue: data.audio_issue || '',
    score_source: data.score_source || '',
    word_scores: (data.word_scores || []).map(w => ({ word: w.word, score: w.score })),
    phone_tips: (data.phone_tips || []).map(t => ({
        word: t.word, expected: t.expected, heard: t.heard, score: t.score,
        expected_label: t.expected_label, heard_label: t.heard_label, hint: t.hint,
        // The card as shown: the whole word spelled out both ways, its kind
        // (silent letter, liaison) and its syllables, so a card can be read
        // back from the log exactly as the learner saw it.
        word_expected: t.word_expected, word_heard: t.word_heard, kind: t.kind,
        syllables: t.syllables,
    })),
    missed_words: data.missed_words || [],
    stress_tips: data.stress_tips || [],
    issues: (data.issues || []).map(i => ({
        word: i.word, expected_ipa: i.expected_ipa, perceived_ipa: i.perceived_ipa, tip: i.tip,
    })),
});
